import React from 'react';
import { motion } from 'framer-motion';
import { Cog, CheckCircle, Settings, Wrench } from 'lucide-react';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import bfpImage from '../../../images/herosection/heromain.jpg';
import bfp1 from '../../../images/BFP/PumpService1.jpg';
import bfp2 from '../../../images/BFP/PumpService2.png';
import EnquiryForm from '../../../components/EnquiryForm/EnquiryForm';
import './BFPService.css';

const BFPService = () => {
  const features = [
    'Pump maintenance and repair',
    'Performance optimization services',
    'Preventive maintenance programs',
    'Reconditioning/refurbishment work',
    'Emergency repair services',
    'Spare parts support',
    'Technical consultation'
  ];

  const services = [
    {
      title: 'Preventive maintenance',
      description: 'Scheduled maintenance to prevent unexpected failures and extend pump life',
      icon: <Wrench className="w-8 h-8" />,
      details: [
        'Regular inspection schedules',
        'Performance monitoring',
        'Predictive maintenance',
        'Documentation and reporting'
      ]
    },
    {
      title: 'Breakdown maintenance',
      description: 'Quick response and repair when pump failures occur',
      icon: <Cog className="w-8 h-8" />,
      details: [
        '24/7 emergency support',
        'Rapid diagnosis and repair',
        'Critical component replacement',
        'Minimal downtime solutions'
      ]
    },
    {
      title: 'Shutdown maintenance',
      description: 'Comprehensive overhaul and maintenance during planned shutdowns',
      icon: <Settings className="w-8 h-8" />,
      details: [
        'Full pump inspection and overhaul',
        'Component replacement and refurbishment',
        'Alignment and balancing',
        'Testing and commissioning'
      ]
    }
  ];

  return (
    <div className="bfp-service-page">
      {/* Hero Section */}
      <section className="bfp-hero" style={{ backgroundImage: `url(${bfpImage})` }}>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bfp-hero-content"
          >
            <Breadcrumb />
            <h1 className="bfp-hero-title">
              Pump <span className="bfp-hero-title-highlight">Services</span>
            </h1>
            <p className="bfp-hero-description">
              Comprehensive Pump Services and maintenance for optimal performance.
              Ensuring reliable operation of your critical pumping systems.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="bfp-overview">
        <div className="container-custom">
          <div className="bfp-overview-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bfp-overview-content"
            >
              <h2 className="bfp-overview-title">Expert Pump Solutions</h2>
              <p className="bfp-overview-description">
                Our specialized team provides comprehensive Pump Services including maintenance, 
                repair, performance optimization, and emergency support. We ensure your critical pumping 
                systems operate at peak efficiency with minimal downtime.
              </p>
              <div className="bfp-features">
                {features.map((feature, index) => (
                  <div key={index} className="bfp-feature-item">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bfp-overview-visual"
            >
              <div className="bfp-image-gallery">
                {[
                  { src: bfp1, alt: 'Pump maintenance', title: 'Expert Maintenance' },
                  { src: bfp2, alt: 'Pump optimization', title: 'Performance Optimization' }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="bfp-image-card"
                  >
                    <div className="bfp-image-card-inner">
                      <img src={item.src} alt={item.alt} />
                      <div className="bfp-image-card-overlay">
                        <h4>{item.title}</h4>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bfp-services">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bfp-services-header"
          >
            <h2 className="bfp-services-title">Our Pump Services</h2>
            <p className="bfp-services-description">
              Comprehensive solutions for pump systems
            </p>
          </motion.div>
          
          <div className="bfp-services-grid">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bfp-service-card"
              >
                <div className="bfp-service-icon">
                  <div className="bfp-service-icon-wrapper">
                    {service.icon}
                  </div>
                </div>
                <h3 className="bfp-service-title">{service.title}</h3>
                <p className="bfp-service-description">{service.description}</p>
                <ul className="bfp-service-details">
                  {service.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="bfp-service-detail-item">
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bfp-cta">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bfp-cta-content"
          >
            <h2 className="bfp-cta-title">Need Pump Support?</h2>
            <p className="bfp-cta-description">
              Get a free consultation and quote by filling out the form below
            </p>
            
            {/* Enquiry Form */}
            <div className="bfp-cta-form">
              <EnquiryForm serviceName="Pump Services" />
            </div>
            
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BFPService;
